import { SetupAssistant } from '__pages/Marketplace/components/SetupAssistant';
import { MarketplacePlugin } from '__pages/Marketplace/types';
import { useCallback } from 'react';
import PluginService from 'services/PluginService';
import { JSONEditorInline } from './components/JSONEditor';
import { processConfig } from '../usePluginEnable';
import { performAssociationLink } from '../association';
import { useMarketplacePermissions } from '../../permissions';

type ConfigurePanelProps = {
  plugin: MarketplacePlugin & {
    base_config: any;
  };
  businessPluginAppsByApiName: Record<string, any>;
  refetch: () => Promise<unknown>;
  businessPluginApp: Record<string, any>;
  canEditJSON: boolean;
};

export const ConfigurePanel = ({
  plugin,
  businessPluginAppsByApiName,
  refetch,
  businessPluginApp,
  canEditJSON,
}: ConfigurePanelProps) => {
  const { canEditIntegrationSecrets } = useMarketplacePermissions();

  const onChangeSetupAssistantConfig = useCallback(
    async (
      pluginApiName: string,
      assistantConfig: any,
      templateAssociations?: Array<any>
    ) => {
      const { newConfig, actionsToLink } = await processConfig(
        pluginApiName,
        assistantConfig,
        businessPluginAppsByApiName,
        canEditIntegrationSecrets
      );

      await PluginService.updateBusinessPluginApp(pluginApiName, {
        config: newConfig,
      });

      await performAssociationLink(
        pluginApiName,
        actionsToLink,
        templateAssociations
      );

      await refetch();
    },
    [refetch, businessPluginAppsByApiName, canEditIntegrationSecrets]
  );

  const onImportKZNFileStep = useCallback(
    async (pluginApiName: string, etag?: string) => {
      const businessPluginApp = businessPluginAppsByApiName[pluginApiName];

      let existingJSONConfig = businessPluginApp?.config;

      if (!existingJSONConfig) {
        existingJSONConfig = {};
      }

      const newConfig = {
        ...existingJSONConfig,
        __kizen_schema_import_etag:
          etag || existingJSONConfig.__kizen_schema_import_etag,
        __kizen_schema_import_skipped: !etag,
        __kizen_schema_import_ts: etag
          ? Date.now()
          : existingJSONConfig.__kizen_schema_import_ts,
      };

      await PluginService.updateBusinessPluginApp(pluginApiName, {
        config: newConfig,
      });

      await refetch();
    },
    [businessPluginAppsByApiName, refetch]
  );

  const hasSetupAssistant = Boolean(plugin.base_config?.setup_assistant);

  if (!hasSetupAssistant && canEditJSON) {
    return (
      <JSONEditorInline
        config={businessPluginApp.config}
        refetch={refetch}
        plugin={plugin}
      />
    );
  }

  return (
    <>
      <SetupAssistant
        config={plugin}
        onChangeConfig={onChangeSetupAssistantConfig}
        businessPluginAppsByApiName={businessPluginAppsByApiName}
        onImportKZNFileStep={onImportKZNFileStep}
        inline
      />
    </>
  );
};
